import { LOCAL } from "@mn/shared";
import { getStroke } from "perfect-freehand";
import { useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useDeepVersion } from "../hooks";
import { useBoardUI } from "../store";

interface Stroke {
  id: string;
  color: string;
  size: number;
  pts: number[]; // flat [x,y,p, x,y,p, ...] in card-local px
}

const COLORS = ["#ffffff", "#f4511c", "#e9c56a", "#55b2a6", "#8aa9ff", "#ff8ac2", "#111111"];
const SIZES = [3, 6, 12, 20];

function pathFor(s: Stroke): string {
  const pts: number[][] = [];
  for (let i = 0; i < s.pts.length; i += 3) pts.push([s.pts[i]!, s.pts[i + 1]!, s.pts[i + 2]!]);
  const outline = getStroke(pts, {
    size: s.size,
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: pts.every((p) => p[2] === 0.5),
  });
  if (outline.length < 2) return "";
  const avg = (a: number, b: number) => (a + b) / 2;
  let d = `M${outline[0]![0]!.toFixed(1)},${outline[0]![1]!.toFixed(1)}`;
  for (let i = 1; i < outline.length; i++) {
    const [x0, y0] = outline[i - 1]!;
    const [x1, y1] = outline[i]!;
    d += ` Q${x0!.toFixed(1)},${y0!.toFixed(1)} ${avg(x0!, x1!).toFixed(1)},${avg(y0!, y1!).toFixed(1)}`;
  }
  return `${d}Z`;
}

/** Vector sketch: strokes are raw input points rendered with perfect-freehand. Double-click to draw, Esc to finish. */
export function SketchBody({
  id,
  ymap,
  w,
  h,
  bare,
}: {
  id: string;
  ymap: Y.Map<unknown>;
  w: number;
  h: number;
  /** Canvas drawing layer: no hint text, dashed outline only while editing. */
  bare?: boolean;
}) {
  const { handle, canEdit } = useBoard();
  const t = useT();
  const strokes = ymap.get("strokes") as Y.Array<Y.Map<unknown>>;
  useDeepVersion(strokes);
  const editing = useBoardUI((s) => s.editingId === id);
  const zoom = useBoardUI((s) => s.camera.z);
  const [color, setColor] = useState(COLORS[0]!);
  const [size, setSize] = useState(SIZES[1]!);
  const [tool, setTool] = useState<"pen" | "eraser">("pen");
  const live = useRef<number[]>([]);
  const [, bump] = useState(0);
  const svgRef = useRef<SVGSVGElement>(null);
  const list: Stroke[] = strokes.toArray().map((m) => m.toJSON() as Stroke);
  const paths = useMemo(() => list.map((s) => ({ id: s.id, color: s.color, d: pathFor(s) })), [list]);

  const local = (e: React.PointerEvent) => {
    const r = svgRef.current!.getBoundingClientRect();
    return [
      (e.clientX - r.left) / zoom,
      (e.clientY - r.top) / zoom,
      e.pointerType === "pen" ? e.pressure : 0.5,
    ];
  };
  const eraseAt = (x: number, y: number) => {
    const hit = list.findIndex((s) => {
      for (let i = 0; i < s.pts.length; i += 3)
        if (Math.hypot(s.pts[i]! - x, s.pts[i + 1]! - y) < s.size / 2 + 8) return true;
      return false;
    });
    if (hit >= 0) handle.doc.transact(() => strokes.delete(hit, 1), LOCAL);
  };

  return (
    <div className="relative" style={{ width: "100%", height: h }}>
      <svg
        ref={svgRef}
        className="block size-full"
        viewBox={`0 0 ${w} ${h}`}
        style={{ touchAction: "none", cursor: editing ? (tool === "pen" ? "crosshair" : "cell") : undefined }}
        onPointerDown={(e) => {
          if (!editing || !canEdit) return;
          e.stopPropagation();
          e.currentTarget.setPointerCapture(e.pointerId);
          const p = local(e);
          if (tool === "eraser") return eraseAt(p[0]!, p[1]!);
          live.current = [...p];
          bump((n) => n + 1);
        }}
        onPointerMove={(e) => {
          if (!editing || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
          const p = local(e);
          if (tool === "eraser") return eraseAt(p[0]!, p[1]!);
          live.current.push(...p);
          bump((n) => n + 1);
        }}
        onPointerUp={(e) => {
          if (!editing) return;
          e.currentTarget.releasePointerCapture(e.pointerId);
          if (tool === "pen" && live.current.length >= 6) {
            const m = new Y.Map<unknown>();
            const pts = live.current.map((v, i) =>
              i % 3 === 2 ? Math.round(v * 100) / 100 : Math.round(v * 10) / 10,
            );
            handle.doc.transact(() => {
              strokes.push([m]);
              m.set("id", crypto.randomUUID());
              m.set("color", color);
              m.set("size", size);
              m.set("pts", pts);
            }, LOCAL);
          }
          live.current = [];
          bump((n) => n + 1);
        }}
        aria-label={t.cardTypes.sketch}
      >
        <title>{t.cardTypes.sketch}</title>
        {paths.map((p) => (
          <path key={p.id} d={p.d} fill={p.color} />
        ))}
        {live.current.length ? (
          <path d={pathFor({ id: "live", color, size, pts: live.current })} fill={color} />
        ) : null}
      </svg>
      {bare && editing ? (
        <div className="pointer-events-none absolute inset-0 border border-dashed border-[#8aa9ff]/60" />
      ) : null}
      {!editing && list.length === 0 && !bare ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12px] text-dim">
          {t.sketchHint}
        </div>
      ) : null}
      {editing && canEdit ? (
        <div
          className="absolute -top-10 left-0 flex items-center gap-1 rounded-sm border border-border bg-panel p-1 shadow-panel"
          onPointerDown={(e) => e.stopPropagation()}
          style={{ transform: `scale(${1 / zoom})`, transformOrigin: "0 100%" }}
        >
          <button
            type="button"
            onClick={() => setTool("pen")}
            className={`size-7 rounded-sm ${tool === "pen" ? "bg-accent text-bg" : "text-muted"}`}
            title="D"
          >
            ✎
          </button>
          <button
            type="button"
            onClick={() => setTool("eraser")}
            className={`size-7 rounded-sm ${tool === "eraser" ? "bg-accent text-bg" : "text-muted"}`}
            title="E"
          >
            ◻
          </button>
          <span className="mx-1 h-5 border-l border-border" />
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={`size-5 rounded-full border ${color === c ? "border-white" : "border-border"}`}
              style={{ background: c }}
              aria-label={c}
            />
          ))}
          <span className="mx-1 h-5 border-l border-border" />
          {SIZES.map((s, i) => (
            <button
              key={s}
              type="button"
              onClick={() => setSize(s)}
              className={`size-7 rounded-sm text-[11px] ${size === s ? "bg-accent text-bg" : "text-muted"}`}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => useBoardUI.getState().setEditing(null)}
            className="ml-1 rounded-sm bg-accent px-2 py-1 text-[11px] font-semibold text-bg"
          >
            {t.done}
          </button>
        </div>
      ) : null}
    </div>
  );
}
